import CrowdfundingBasics, { I18N_CONTEXT } from './crowdfunding_basics'
import { reduxForm } from 'redux-form'
import formUtils from 'lib/form_utils'
import {
  combine,
  required,
  futureDate,
  atMost,
  atLeast,
  length,
} from 'lib/validators'
import { endDateValidators, maxDaysValidator } from 'lib/end_date_validators'
import { TITLE_LENGTH } from 'shared/lengths'
import { push } from 'react-router-redux'
import { NEW_CAMPAIGN_ID, setData } from 'redux/modules/campaign'
import { getCampaignSelector, isGNSelector } from 'redux/selectors/campaign'
import { getI18n } from 'client/i18n'
import { formatMoney } from 'client/griffin'
import {
  POOLS_CAMPAIGN_TYPES,
  CROWDFUNDING_GOAL_AMOUNT,
  CROWDFUNDING_CAMPAIGN_MAX_DAYS,
  GN_DISCLAIMERS,
} from 'shared/shared_consts'
import { trackClick } from 'lib/fpti_analytics'

const i18n = getI18n(I18N_CONTEXT)
const { CROWD_FUNDING } = POOLS_CAMPAIGN_TYPES

const addDays = (date, days) => {
  const copy = new Date(Number(date.valueOf()))
  copy.setDate(date.getDate() + days)
  return copy
}

const initialDate = new Date()
const defaultDate = addDays(initialDate, CROWDFUNDING_CAMPAIGN_MAX_DAYS)

const mapStateToProps = (state, props) => {
  const countryCode = state.country.country_code
  const { consumerLink, merchantLink, termsAndConditions } = GN_DISCLAIMERS[
    countryCode
  ]
  const campaign = getCampaignSelector(state, props)

  let MIN_AMOUNT
  let MAX_AMOUNT
  let formattedMaxGoalAmount
  let formattedMinGoalAmount = formatMoney(
    campaign.currency,
    state.flags.min_amount_donation,
    {
      removeTrailingZeros: true,
    }
  )

  let validators = [
    required('endDate', i18n('end_date_required')),
    futureDate('endDate', i18n('date_must_be_future')),
    length(
      'poolName',
      TITLE_LENGTH,
      i18n('pool_name_too_long', { maxCharLength: TITLE_LENGTH })
    ),
    required(
      'poolName',
      i18n('pool_name_is_required', { maxCharLength: TITLE_LENGTH })
    ),
    ...endDateValidators('endDate', i18n('invalid_date')),
  ]

  if (isGNSelector(state, props)) {
    MIN_AMOUNT = CROWDFUNDING_GOAL_AMOUNT[countryCode].MIN_AMOUNT
    MAX_AMOUNT = CROWDFUNDING_GOAL_AMOUNT[countryCode].MAX_AMOUNT
    formattedMaxGoalAmount = formatMoney(campaign.currency, MAX_AMOUNT, {
      removeTrailingZeros: true,
    })
    formattedMinGoalAmount = formatMoney(campaign.currency, MIN_AMOUNT, {
      removeTrailingZeros: true,
    })

    validators.push(
      atMost(
        'goalAmount',
        MAX_AMOUNT,
        i18n('goal_amount_invalid', {
          amount: formattedMaxGoalAmount,
        })
      )
    )
  }

  return {
    validate: combine(
      ...validators,
      required(
        'goalAmount',
        i18n('goal_amount_required', {
          amount: formattedMinGoalAmount,
        })
      ),
      atLeast(
        'goalAmount',
        MIN_AMOUNT || state.flags.min_amount_donation,
        i18n('goal_amount_invalid_min', {
          amount: formattedMinGoalAmount,
        })
      ),
      maxDaysValidator(
        'endDate',
        new Date(),
        CROWDFUNDING_CAMPAIGN_MAX_DAYS,
        i18n('invalid_date_limit', {
          maxdays: CROWDFUNDING_CAMPAIGN_MAX_DAYS,
        })
      )
    ),
    initialValues: {
      poolName: campaign.title,
      goalAmount: campaign.goal,
      endDate: campaign.end_date ? new Date(campaign.end_date) : defaultDate,
      charity: campaign.charity,
    },
    formattedMaxGoalAmount,
    charity: campaign.charity,
    disclaimer: i18n(`disclaimer_gn_${countryCode}`, {
      consumerLink: consumerLink,
      merchantLink: merchantLink,
      ...(['GB', 'DE', 'CA'].includes(countryCode)
        ? { termsAndConditionsLink: termsAndConditions }
        : {}),
    }),
  }
}

const mapDispatchToProps = dispatch => ({
  onSubmit(formValues) {
    trackClick({
      clickName: 'next',
      data: {
        flow: formValues.charity
          ? 'GENEROSITY_NETWORK_CHARITIES'
          : 'GENEROSITY_NETWORK',
      },
    })
    dispatch(
      setData(NEW_CAMPAIGN_ID, {
        title: formValues.poolName,
        goal: formValues.goalAmount,
        end_date: formValues.endDate,
        campaign_type: CROWD_FUNDING,
      })
    )
    dispatch(
      push({
        pathname: `${GLOBAL_CONSTS.REQUEST_URI}/c/create-crowdfunding/story`,
      })
    )
  },
})

export default reduxForm(
  {
    form: 'crowdfunding_basics',
    destroyOnUnmount: false,
    touchOnBlur: true,
    touchOnChange: false,
    fields: ['poolName', 'goalAmount', 'endDate', 'charity'],
  },
  mapStateToProps,
  mapDispatchToProps
)(formUtils(CrowdfundingBasics))
